import { useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import {
  BookOpen,
  CheckCircle2,
  Copy,
  ExternalLink,
  History,
  KeyRound,
  Loader2,
  Plug,
  PlugZap,
  Save,
  XCircle,
} from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Tabs } from '@components/Tabs';
import { MarkdownView } from '@components/help/MarkdownView';
import { DOCS } from '@/docs';
import { integrationGuideText } from '@/utils/integrationGuides';
import adminService from '@services/adminService';
import type { Integration, IntegrationHistory, TestResult } from '@/types/admin';
import { formatDateTimeBR } from '@/utils/formatters';

const SOURCE_LABELS: Record<string, { label: string; cls: string }> = {
  table: { label: 'tabela', cls: 'badge-success' },
  env: { label: 'ambiente (provisório)', cls: 'badge-warning' },
  default: { label: 'padrão', cls: 'badge-neutral' },
  empty: { label: 'vazio', cls: 'badge-neutral' },
};

const copy = (text: string) => {
  navigator.clipboard?.writeText(text).catch(() => undefined);
};

/** Um cartão por integração: campos (segredos em claro), salvar, testar e histórico. */
const IntegrationCard: React.FC<{ integration: Integration; onSaved: (i: Integration) => void; onError: (e: unknown) => void }> = ({
  integration,
  onSaved,
  onError,
}) => {
  const initial = useMemo(
    () => Object.fromEntries(integration.fields.map((f) => [f.key, f.source === 'table' || f.source === 'env' ? f.value : ''])),
    [integration],
  );
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);
  const [history, setHistory] = useState<IntegrationHistory[] | null>(null);

  useEffect(() => setValues(initial), [initial]);

  const changed = integration.fields.filter((f) => (values[f.key] ?? '') !== (initial[f.key] ?? ''));
  // Valor ainda vindo do ambiente também é gravado ao salvar (sai do Secret para a tabela).
  const fromEnv = integration.fields.filter((f) => f.source === 'env');

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (const f of [...changed, ...fromEnv]) payload[f.key] = (values[f.key] ?? '').trim();
      const saved = await adminService.saveIntegration(integration.key, payload);
      onSaved(saved);
      setTest(null);
      if (history) setHistory(await adminService.integrationHistory(integration.key));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      setTest(await adminService.testIntegration(integration.key));
    } catch (err) {
      onError(err);
    } finally {
      setTesting(false);
    }
  };

  const toggleHistory = async () => {
    if (history) {
      setHistory(null);
      return;
    }
    try {
      setHistory(await adminService.integrationHistory(integration.key));
    } catch (err) {
      onError(err);
    }
  };

  return (
    <section className="card p-4 space-y-3" id={`integration-${integration.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-h4 flex items-center gap-2">
            {integration.configured ? (
              <PlugZap size={18} className="text-emerald-600" />
            ) : (
              <Plug size={18} className="text-slate-400" />
            )}
            {integration.name}
          </h2>
          <p className="text-small text-slate-500 mt-1">{integration.description}</p>
          {!integration.configured && integration.missing.length > 0 && (
            <p className="text-small text-amber-700 mt-1">Falta preencher: {integration.missing.join(', ')}</p>
          )}
        </div>
        {integration.docs_url && (
          <a
            href={integration.docs_url}
            target="_blank"
            rel="noreferrer"
            className="text-small text-emerald-700 hover:underline inline-flex items-center gap-1"
          >
            Documentação do fornecedor <ExternalLink size={12} />
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {integration.fields.map((f) => (
          <label key={f.key} className="block">
            <span className="text-label flex items-center gap-1.5">
              {f.secret && <KeyRound size={13} className="text-amber-600" />}
              {f.label}
              {f.required && <span className="text-red-500">*</span>}
              <span className={clsx(SOURCE_LABELS[f.source]?.cls, 'ml-auto text-[10px]')}>{SOURCE_LABELS[f.source]?.label}</span>
            </span>
            <div className="flex gap-1 mt-1">
              <input
                className="input-field font-mono text-sm"
                value={values[f.key] ?? ''}
                placeholder={f.default ? `padrão: ${f.default}` : ''}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
              <button
                type="button"
                className="btn-outline px-2"
                title="Copiar"
                onClick={() => copy(values[f.key] ?? '')}
                disabled={!values[f.key]}
              >
                <Copy size={14} />
              </button>
            </div>
            {f.help && <span className="text-small text-slate-500 mt-0.5 block [overflow-wrap:anywhere]">{f.help}</span>}
            {f.updated_at && (
              <span className="text-[11px] text-slate-400 block">
                Alterado em {formatDateTimeBR(f.updated_at)} por {f.updated_by}
              </span>
            )}
          </label>
        ))}
      </div>

      {test && (
        <div
          className={clsx(
            'rounded-lg px-3 py-2 text-sm flex items-start gap-2',
            test.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800',
          )}
        >
          {test.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
          <span className="break-all">{test.message}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          icon={<Save />}
          loading={saving}
          disabled={changed.length === 0 && fromEnv.length === 0}
          onClick={save}
        >
          {fromEnv.length > 0 && changed.length === 0 ? 'Gravar na tabela' : 'Salvar'}
        </Button>
        {integration.testable && (
          <Button size="sm" variant="outline" loading={testing} onClick={runTest} disabled={changed.length > 0}>
            Testar conexão
          </Button>
        )}
        {changed.length > 0 && integration.testable && (
          <span className="text-small text-slate-500">Salve antes de testar.</span>
        )}
        <button type="button" className="ml-auto text-small text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" onClick={toggleHistory}>
          <History size={14} /> {history ? 'Ocultar histórico' : 'Histórico'}
        </button>
      </div>

      {history && (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          {history.length === 0 ? (
            <p className="p-3 text-small text-slate-500">Nenhuma alteração registrada.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="table-header">
                <tr>
                  <th>Quando</th>
                  <th>Quem</th>
                  <th>Chave</th>
                  <th>Anterior</th>
                  <th>Novo</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="table-row">
                    <td className="table-cell whitespace-nowrap">{formatDateTimeBR(h.changed_at)}</td>
                    <td className="table-cell">{h.changed_by}</td>
                    <td className="table-cell font-mono">{h.key}</td>
                    <td className="table-cell font-mono break-all max-w-xs">{h.old_value ?? '—'}</td>
                    <td className="table-cell font-mono break-all max-w-xs">{h.new_value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
};

/** Admin → Integrações: toda integração externa é configurada aqui (só admin). */
export const IntegrationsView: React.FC = () => {
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminService.listIntegrations());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Uma aba por integração (na ordem do catálogo): o painel de configuração e o passo a passo
  // do fornecedor (src/docs/guias — fonte única, também na Central de ajuda).
  const [params, setParams] = useSearchParams();
  const tabs = useMemo(
    () =>
      items.map((it) => {
        const guide = integrationGuideText(it.key, DOCS);
        return {
          id: it.key,
          label: it.name,
          icon: it.configured ? <PlugZap size={14} className="text-emerald-600" /> : <Plug size={14} />,
          content: (
            <div className="space-y-4">
              <p className="text-label uppercase tracking-wide text-slate-500">{it.group}</p>
              <IntegrationCard
                integration={it}
                onError={setError}
                onSaved={(saved) => setItems((list) => list.map((x) => (x.key === saved.key ? saved : x)))}
              />
              {guide && (
                <section className="card p-4 sm:p-5">
                  <h2 className="text-h4 flex items-center gap-2 mb-3">
                    <BookOpen size={18} className="text-slate-500" /> Passo a passo
                  </h2>
                  <MarkdownView source={guide} />
                </section>
              )}
            </div>
          ),
        };
      }),
    [items],
  );
  const current = params.get('tab') ?? undefined;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h3 flex items-center gap-2">
          <Plug size={20} className="text-slate-500" /> Integrações
        </h1>
        <p className="text-small text-slate-500 mt-1">
          Credenciais e parâmetros de todos os serviços externos, um por aba, com o passo a passo de cada fornecedor. Os
          valores ficam no banco e valem sem reiniciar o sistema (até 1 minuto para o processamento de mídias). Senhas e
          tokens aparecem em claro: esta tela é exclusiva de administradores.
        </p>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : (
        tabs.length > 0 && (
          <Tabs
            key={tabs.length}
            tabs={tabs}
            defaultTab={current && tabs.some((t) => t.id === current) ? current : tabs[0].id}
            onTabChange={(id) => setParams({ tab: id }, { replace: true })}
          />
        )
      )}
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
